"use client";

import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { Dialog } from "./Dialog";
import { FieldLabel } from "./FieldLabel";
import { Glyph } from "./Glyph";
import { Select } from "./Select";
import { TextInput } from "./inputs";
import type { Assertion } from "@/server/services/playground";
import { duplicateArgKeysOf, hasDuplicateArgKeys, isUnparsableNumberValue, toAssertions, toDraftAssertions, type DraftArgRow, type DraftAssertion } from "./sandboxAssertionDraft.utils";
const MAX_ASSERTIONS = 10;
const BOOLEAN_LITERAL_VALUES = new Set(["true", "false"]);
const hasUnparsableNumberRow = (draftAssertions: DraftAssertion[]): boolean => draftAssertions.some(draft => draft.argRows.some(row => !row.isRef && row.literalType === "number" && isUnparsableNumberValue(row.literalValue)));
const emptyArgRow = (): DraftArgRow => ({
  id: uuidv4(),
  argKey: "",
  isRef: false,
  literalType: "string",
  literalValue: "",
  fromTool: "",
  path: ""
});
const emptyDraftAssertion = (): DraftAssertion => ({
  id: uuidv4(),
  tool: "",
  argRows: []
});
interface SandboxAssertionEditorProps {
  assertions: Assertion[];
  onSetAssertions: (assertions: Assertion[]) => void;
  isDisabled?: boolean;
}
function DraftArgRowEditor({
  row,
  isDuplicateKey,
  onChange,
  onRemove
}: {
  row: DraftArgRow;
  isDuplicateKey: boolean;
  onChange: (patch: Partial<DraftArgRow>) => void;
  onRemove: () => void;
}) {
  const t = useClientTranslations("sandbox");
  const hasInvalidNumber = !row.isRef && row.literalType === "number" && isUnparsableNumberValue(row.literalValue);
  return <div className="flex flex-col gap-2 rounded-field bg-offwhite px-3 py-2.5" data-sentry-component="DraftArgRowEditor" data-sentry-source-file="SandboxAssertionEditor.tsx">
            <div className="flex items-center gap-2">
                <TextInput size="sm" value={row.argKey} placeholder={t("assertions.argKeyPlaceholder")} aria-label={t("assertions.argKeyLabel")} onChange={event => onChange({
        argKey: event.target.value
      })} className="flex-1" data-sentry-element="TextInput" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                <Checkbox checked={row.isRef} onCheckedChange={isRef => onChange({
        isRef
      })} aria-label={t("assertions.useReferenceLabel")} data-sentry-element="Checkbox" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                <FieldLabel className="font-normal text-ink-2" data-sentry-element="FieldLabel" data-sentry-source-file="SandboxAssertionEditor.tsx">
                    {t("assertions.useReferenceLabel")}
                </FieldLabel>
                <Button variant="ghost" size="icon" onClick={onRemove} aria-label={t("assertions.removeArgument")} data-sentry-element="Button" data-sentry-source-file="SandboxAssertionEditor.tsx">
                    <Glyph name="trash" data-sentry-element="Glyph" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                </Button>
            </div>
            {isDuplicateKey && <p className="text-footnote text-tone-red-ink">
                    {t("assertions.duplicateArgumentError")}
                </p>}
            {row.isRef ? <div className="flex items-center gap-2">
                    <TextInput size="sm" value={row.fromTool} placeholder={t("assertions.fromToolPlaceholder")} aria-label={t("assertions.fromToolLabel")} onChange={event => onChange({
        fromTool: event.target.value
      })} className="flex-1" />
                    <TextInput size="sm" value={row.path} placeholder={t("assertions.pathPlaceholder")} aria-label={t("assertions.pathLabel")} onChange={event => onChange({
        path: event.target.value
      })} className="flex-1" />
                </div> : <div className="flex items-center gap-2">
                    <Select value={row.literalType} options={[{
        value: "string",
        label: t("assertions.literalTypeString")
      }, {
        value: "number",
        label: t("assertions.literalTypeNumber")
      }, {
        value: "boolean",
        label: t("assertions.literalTypeBoolean")
      }]} onChange={literalType => onChange({
        literalType,
        ...(literalType === "boolean" && !BOOLEAN_LITERAL_VALUES.has(row.literalValue) ? {
          literalValue: "true"
        } : {}),
        ...(literalType === "number" && isUnparsableNumberValue(row.literalValue) ? {
          literalValue: ""
        } : {})
      })} label={t("assertions.literalTypeLabel")} />
                    {row.literalType === "boolean" ? <Select value={BOOLEAN_LITERAL_VALUES.has(row.literalValue) ? row.literalValue : "true"} options={[{
        value: "true",
        label: t("assertions.literalValueBooleanTrue")
      }, {
        value: "false",
        label: t("assertions.literalValueBooleanFalse")
      }]} onChange={literalValue => onChange({
        literalValue
      })} label={t("assertions.literalValueLabel")} /> : <div className="flex flex-1 flex-col gap-1">
                            <TextInput size="sm" type={row.literalType === "number" ? "number" : "text"} value={row.literalValue} placeholder={t("assertions.literalValuePlaceholder")} aria-label={t("assertions.literalValueLabel")} onChange={event => onChange({
          literalValue: event.target.value
        })} />
                            {hasInvalidNumber && <p className="text-footnote text-tone-red-ink">
                                    {t("assertions.invalidNumberError")}
                                </p>}
                        </div>}
                </div>}
        </div>;
}
function DraftAssertionEditor({
  draft,
  onChange,
  onRemove
}: {
  draft: DraftAssertion;
  onChange: (patch: Partial<DraftAssertion>) => void;
  onRemove: () => void;
}) {
  const t = useClientTranslations("sandbox");
  const updateArgRow = (rowId: string, patch: Partial<DraftArgRow>) => {
    onChange({
      argRows: draft.argRows.map(row => row.id === rowId ? {
        ...row,
        ...patch
      } : row)
    });
  };
  const removeArgRow = (rowId: string) => {
    onChange({
      argRows: draft.argRows.filter(row => row.id !== rowId)
    });
  };
  const addArgRow = () => {
    onChange({
      argRows: [...draft.argRows, emptyArgRow()]
    });
  };
  const duplicateKeys = duplicateArgKeysOf(draft.argRows);
  return <div className="flex flex-col gap-2.5 rounded-card bg-surface p-3.5 shadow-hairline" data-sentry-component="DraftAssertionEditor" data-sentry-source-file="SandboxAssertionEditor.tsx">
            <div className="flex items-center gap-2">
                <TextInput value={draft.tool} placeholder={t("assertions.toolPlaceholder")} aria-label={t("assertions.toolLabel")} onChange={event => onChange({
        tool: event.target.value
      })} className="flex-1" data-sentry-element="TextInput" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                <Button variant="ghost" size="icon" onClick={onRemove} aria-label={t("assertions.removeAssertion")} data-sentry-element="Button" data-sentry-source-file="SandboxAssertionEditor.tsx">
                    <Glyph name="trash" data-sentry-element="Glyph" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                </Button>
            </div>
            {draft.argRows.map(row => <DraftArgRowEditor key={row.id} row={row} isDuplicateKey={duplicateKeys.has(row.argKey.trim())} onChange={patch => updateArgRow(row.id, patch)} onRemove={() => removeArgRow(row.id)} />)}
            <Button variant="ghost" size="sm" onClick={addArgRow} data-sentry-element="Button" data-sentry-source-file="SandboxAssertionEditor.tsx">
                <Glyph name="plus" data-sentry-element="Glyph" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                {t("assertions.addArgument")}
            </Button>
        </div>;
}
export function SandboxAssertionEditor({
  assertions,
  onSetAssertions,
  isDisabled
}: SandboxAssertionEditorProps) {
  const t = useClientTranslations("sandbox");
  const [isOpen, setIsOpen] = useState(false);
  const [draftAssertions, setDraftAssertions] = useState<DraftAssertion[]>([]);
  const openEditor = () => {
    setDraftAssertions(toDraftAssertions(assertions));
    setIsOpen(true);
  };
  const closeEditor = () => setIsOpen(false);
  const handleSave = () => {
    onSetAssertions(toAssertions(draftAssertions));
    closeEditor();
  };
  const updateDraft = (id: string, patch: Partial<DraftAssertion>) => {
    setDraftAssertions(prev => prev.map(draft => draft.id === id ? {
      ...draft,
      ...patch
    } : draft));
  };
  const removeDraft = (id: string) => {
    setDraftAssertions(prev => prev.filter(draft => draft.id !== id));
  };
  const addDraft = () => {
    setDraftAssertions(prev => prev.length >= MAX_ASSERTIONS ? prev : [...prev, emptyDraftAssertion()]);
  };
  return <>
            <Button variant="ghost" size="sm" onClick={openEditor} disabled={isDisabled} data-sentry-element="Button" data-sentry-source-file="SandboxAssertionEditor.tsx">
                {t("assertions.trigger", {
        count: assertions.length
      })}
            </Button>
            <Dialog isOpen={isOpen} onClose={closeEditor} title={t("assertions.dialogTitle")} description={t("assertions.dialogDescription")} footer={<>
                        <Button variant="outline" size="sm" onClick={closeEditor}>
                            {t("assertions.cancel")}
                        </Button>
                        <Button variant="primary" size="sm" onClick={handleSave} disabled={hasUnparsableNumberRow(draftAssertions) || hasDuplicateArgKeys(draftAssertions)}>
                            {t("assertions.save")}
                        </Button>
                    </>} data-sentry-element="Dialog" data-sentry-source-file="SandboxAssertionEditor.tsx">
                <div className="flex flex-col gap-3">
                    {draftAssertions.map(draft => <DraftAssertionEditor key={draft.id} draft={draft} onChange={patch => updateDraft(draft.id, patch)} onRemove={() => removeDraft(draft.id)} />)}
                    <Button variant="ghost" size="sm" onClick={addDraft} disabled={draftAssertions.length >= MAX_ASSERTIONS} data-sentry-element="Button" data-sentry-source-file="SandboxAssertionEditor.tsx">
                        <Glyph name="plus" data-sentry-element="Glyph" data-sentry-source-file="SandboxAssertionEditor.tsx" />
                        {t("assertions.addAssertion")}
                    </Button>
                </div>
            </Dialog>
        </>;
}